/*
 * ecom-collection-algolia-lazy.js  (Algolia usage patch, 2026-09-30)
 *
 * Replaces the inline collection <script> in the hybrid Algolia collection
 * section (the block containing "[Algolia Debug]" and
 * "getElementById('algolia-collection').getAttribute('data-collection-handle')").
 *
 * Behaviour:
 *   - NO Algolia request on page load. The Liquid-rendered first page
 *     (#ssr-hits, #ssr-pagination, #product-count) is what visitors and
 *     crawlers see.
 *   - InstantSearch starts only when the visitor interacts with the filters
 *     (click / tap / keyboard focus in the filter sidebar, the mobile
 *     "Filters" button, a "Show filters" placeholder, or the optional
 *     #algolia-start button), OR immediately if the URL already carries
 *     Algolia routing state (?shopify_products[...]) - e.g. a shared
 *     filtered link.
 *   - The live Algolia grid (#algolia-hits / #algolia-pagination) replaces
 *     the Liquid grid only once the visitor has actually refined, paged,
 *     or arrived with URL state; before that the Liquid grid stays visible.
 *   - Crawler guard: bots / headless / webdriver never start InstantSearch.
 *   - Add to Cart works for both Liquid and Algolia cards (delegated on
 *     #algolia-collection).
 *   - Works with or without the SSR markup (if #ssr-hits is absent the live
 *     grid is shown as soon as InstantSearch starts).
 *
 * Unchanged from the live script: filters (33 refinement lists with
 * sizeSort / gradeSort), card template, pagination, distinct:true,
 * collection filter, compare checkboxes, add-to-cart toast.
 *
 * 2026-10-08 search & facets Phase 1 (SEARCH_FACETS_REVIEW.md, approved by Brian 09:07 PT). Needs
 * assets/ecom-facets-core.js (window.EcomFacets) loaded before this file. Still 0 Algolia calls on page load.
 *   item 1/3  per-family rail order + hidden facets ...... EcomFacets.Rail, applied after every render
 *   item 2    Inch | Metric toggle above the rail ........ #ecom-unit-toggle (replaces the Measurement System list)
 *   item 4/5  full-list size sort, merged metric values .. filterWidget -> EcomFacets.facetWidget
 *   item 6    find boxes filter locally .................. no `searchable` widget is left: 0 searchForFacetValues
 *   item 7    sort control (Relevance only, from first paint; Mira 2026-10-08) .... #ecom-sort-select / #ecom-sort-pills
 *   item 8    mobile sheet = dialog, 44px close, footer .. EcomFacets.drawer + #ecom-drawer-show count
 *   item 9    card pack line + $/pc (ecom-ppp.js rules) .. renderCard + EcomFacets.fillPPP (SSR cards too)
 */
(function() {
  'use strict';

  var root = document.getElementById('algolia-collection');
  if (!root) return;
  var FX = window.EcomFacets || null;   // P1 2026-10-08: assets/ecom-facets-core.js (loaded by the section first)

  var CONFIG = {
    appId: 'SKNBBZXEDC',
    // 2026-10-03: no hard-coded key. The search-only key comes from the Algolia app config
    // (window.algoliaShopify.config.search_api_key, rendered on every page by the app embed).
    // If it is missing, filters show "temporarily unavailable" and the Liquid grid stays.
    indexName: 'shopify_products'
  };

  var LIBS = [
    { test: function () { return typeof window.algoliasearch === 'function'; },
      src: 'https://cdn.jsdelivr.net/npm/algoliasearch@4.20.0/dist/algoliasearch-lite.umd.js' },
    { test: function () { return typeof window.instantsearch === 'function'; },
      src: 'https://cdn.jsdelivr.net/npm/instantsearch.js@4.56.0/dist/instantsearch.production.min.js' }
  ];

  var IS_BOT = (function () {
    try {
      if (navigator.webdriver) return true;
      return /bot|crawl|spider|slurp|headless|lighthouse/i.test(navigator.userAgent || '');
    } catch (e) { return false; }
  })();

  var collectionHandle = root.getAttribute('data-collection-handle') || '';
  // Optional: add data-hits-per-page="{{ ... }}" on #algolia-collection; live value is 24.
  var HITS_PER_PAGE = parseInt(root.getAttribute('data-hits-per-page'), 10) || 24;

  var liveHits = document.getElementById('algolia-hits');
  var livePag  = document.getElementById('algolia-pagination');
  var ssrHits  = document.getElementById('ssr-hits');
  var ssrPag   = document.getElementById('ssr-pagination');
  var sidebar  = document.getElementById('filters-sidebar');
  var backdrop = document.getElementById('filters-backdrop');

  var search = null;
  var startPromise = null;
  var liveShown = !ssrHits; // no Liquid grid on the page -> show live grid as soon as it renders

  function hasAlgoliaUrlState() {
    return /(?:^|[?&])shopify_products(?:%5B|\[)/i.test(window.location.search);
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src; s.async = false;
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  function ensureLibs() {
    return LIBS.reduce(function (p, lib) {
      return p.then(function () { return lib.test() ? null : loadScript(lib.src); });
    }, Promise.resolve());
  }
  function getSearchKey() {
    var c = window.algoliaShopify && window.algoliaShopify.config;
    return (c && c.search_api_key) || '';
  }

  function showLive() {
    if (ssrHits) ssrHits.style.display = 'none';
    if (ssrPag)  ssrPag.style.display = 'none';
    if (liveHits) liveHits.style.display = '';
    if (livePag)  livePag.style.display = '';
    root.classList.add('is-algolia-live');
    liveShown = true;
  }

  function userHasRefined() {
    if (!search) return false;
    var ui = (search.getUiState() || {})[CONFIG.indexName] || {};
    var rl = ui.refinementList || {};
    var anyRefinement = Object.keys(rl).some(function (k) { return rl[k] && rl[k].length; });
    return anyRefinement || (ui.page && ui.page > 1) || !!ui.query || !!ui.menu || !!ui.range || !!ui.sortBy;
  }

  // HTML escape helper (from ECOM v4)
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  
  // Filter option template matching existing design
  function filterOptionTemplate(item) {
    return '<label class="ecom-filter-option ' + (item.isRefined ? 'is-active' : '') + '">' +
      '<input type="checkbox" ' + (item.isRefined ? 'checked' : '') + ' style="position:absolute;opacity:0;pointer-events:none;">' +
      '<span class="ecom-filter-option__check">' +
        '<svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</span>' +
      '<span class="ecom-filter-option__label">' + item.label + '</span>' +
      '<span class="ecom-filter-option__count">(' + item.count.toLocaleString() + ')</span>' +
    '</label>';
  }
  
  // Product card template (renderCard from ECOM v4)
 function renderCard(hit) {
  var material = hit.material || (hit.meta && hit.meta.custom && hit.meta.custom.material) || '';
  var grade = hit.grade || (hit.meta && hit.meta.custom && hit.meta.custom.grade) || '';
  var finish = hit.finish || (hit.meta && hit.meta.custom && hit.meta.custom.finish) || '';

  var specs = '';
  if (material) specs += '<span class="ae-pill ae-pill-mat">' + escapeHtml(material) + '</span>';
  if (grade)    specs += '<span class="ae-pill ae-pill-grd">' + escapeHtml(grade) + '</span>';
  if (finish)   specs += '<span class="ae-pill ae-pill-fin">' + escapeHtml(finish) + '</span>';

  var imgHtml;
  var imgSrc = hit.image ? hit.image.replace(/\.([^.]+)$/, '_400x400.$1') : '';
  if (hit.image) {
    imgHtml = '<img src="' + imgSrc + '" alt="' + escapeHtml(hit.title) + '" loading="lazy">';
  } else {
    imgHtml = '<svg width="48" height="48" viewBox="0 0 24 24" fill="#d1d5db"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
  }

  // Use the product's price range rather than the single matched variant's
  // price, since after distinct:true the representative hit may not be
  // the cheapest/default variant. Falls back to hit.price if range fields
  // are missing for any reason.
  var minPrice = (hit.variants_min_price !== undefined && hit.variants_min_price !== null) ? hit.variants_min_price : hit.price;
  var maxPrice = (hit.variants_max_price !== undefined && hit.variants_max_price !== null) ? hit.variants_max_price : hit.price;
  var priceHtml;
  if (minPrice !== undefined && minPrice !== null) {
    if (maxPrice && maxPrice > minPrice) {
      priceHtml = '$' + Number(minPrice).toFixed(2) + ' – $' + Number(maxPrice).toFixed(2);
    } else {
      priceHtml = '$' + Number(minPrice).toFixed(2);
    }
  } else {
    priceHtml = '';
  }

  var variantId = hit.objectID;
  var productId = hit.handle || hit.objectID;
  // RFQ canary 2026-10-09: 'rfq' tag -> price on request + quote link; EcomRFQ.FLAGS.oosCardFix -> sold-out records get View Product
  var RQ = window.EcomRFQ, cardMode = RQ ? RQ.cardMode(hit) : 'atc';
  if (cardMode === 'rfq') priceHtml = RQ.PRICE_TEXT;

  return '<article class="ae-card" data-ef-product data-ef-id="' + escapeHtml(productId) + '" data-ef-handle="' + escapeHtml(hit.handle) + '" data-ef-title="' + escapeHtml(hit.title) + '" data-ef-image="' + escapeHtml(imgSrc) + '" data-ef-price="' + escapeHtml(priceHtml) + '" data-ef-url="/products/' + escapeHtml(hit.handle) + '">' +
    '<a href="/products/' + hit.handle + '">' +
      '<div class="ae-card-img">' + imgHtml + '</div>' +
      '<div class="ae-card-body">' +
        (specs ? '<div class="ae-specs">' + specs + '</div>' : '') +
        '<div class="ae-sku">' + escapeHtml(hit.sku || '') + '</div>' +
        '<h3 class="ae-card-title">' + escapeHtml(hit.title) + '</h3>' +
        '<div class="ae-price">' + priceHtml + '</div>' +
        // P1 item 9: name the pack this card stands for (+ $/pc where the PDP rules allow it; filled by fillPPP)
        (cardMode === 'rfq' ? '<div class="ae-pack ae-pack--rfq"><span class="ae-pack__name">Available on request</span></div>' : (FX ? FX.packLineHtml(hit) : '')) +
      '</div>' +
    '</a>' +
    '<div class="ef-compare-wrapper">' +
      '<label class="ef-compare-label">' +
        '<input type="checkbox" class="ef-compare-checkbox" data-product-id="' + escapeHtml(productId) + '">' +
        '<span class="ef-compare-label-text">Compare</span>' +
      '</label>' +
    '</div>' +
    '<div class="ae-card-footer">' +
      (cardMode !== 'atc' ? RQ.footerHtml(hit, cardMode) :
      '<button class="ae-atc-btn" data-variant-id="' + variantId + '">' +
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>' +
        'Add to Cart' +
      '</button>') +
    '</div>' +
  '</article>';
}
  
  /* -------------------------------------------------------------------------
     P1 2026-10-08 (items 4, 5, 6): sizeSort / gradeSort moved to assets/ecom-facets-core.js
     (EcomFacets.compareSize / compareGrade, same rules plus metric coarse-first) and now sort the WHOLE value
     list before it is cut. The old version ran in transformItems, i.e. AFTER InstantSearch had already cut the
     list to the 12 most common values, so 1/2", 5/8", 3/4" hid behind "Show more".
     filterWidget keeps its signature but renders through EcomFacets.facetWidget:
       opts.sort    : 'size' | 'grade' | 'count' | 'series' | 'name'  (count = biggest first, doc §5.1)
       opts.find    : placeholder of a LOCAL find box (filters loaded values; never searchForFacetValues)
       opts.limit   : rows shown before "Show all N"
       opts.relabel : { rawValue: 'Display text' } -- display only; refinement uses the raw value
       opts.noun    : word after the count on the "Show all" button
  ------------------------------------------------------------------------- */
  function filterWidget(container, key, opts) {
    opts = opts || {};
    return FX.facetWidget(window.instantsearch, {
      container: '#filter-' + container,
      attribute: key.indexOf('.') >= 0 || key === 'product_type' ? key : 'meta.custom.' + key,
      sort: opts.sort || 'name',
      limit: opts.limit || 12,
      find: opts.find,
      relabel: opts.relabel,
      noun: opts.noun,
      tiles: opts.tiles   // Batch C: icon tile picker
    });
  }

  // P1 §9: human facet names for the chips' screen-reader label; merged metric values = one chip
  var CHIP_NAMES = { thread_size: 'Thread size', length_nominal_: 'Length', diameter_nominal_: 'Diameter', material: 'Material',
    grade: 'Grade', finish: 'Finish', head_style: 'Head style', drive_type: 'Drive', thread_series: 'Thread series',
    thread_coverage: 'Thread coverage', standard: 'Standard', measurement_system: 'System', product_type: 'Product type',
    nut_type: 'Nut type', washer_type: 'Washer type', made_in_usa: 'Made in USA' };
  var MERGE_ATTRS = ['thread_size', 'length_nominal_', 'diameter_nominal_', 'outer_diameter', 'inside_diameter', 'thickness',
    'width_across_flats', 'grip_range', 'rivet_size'];
  function chipsTransform() {
    return FX.chipsTransform(CHIP_NAMES, MERGE_ATTRS, function () { return search.mainIndex.getHelper(); });
  }

  // Build widgets (called lazily, after the libraries are available)
  function buildWidgets() {
  return [
    // Stats
    instantsearch.widgets.stats({
      container: '#product-count',
      templates: {
        text: function(data) {
          return '<strong>' + data.nbHits.toLocaleString() + '</strong> products';
        }
      }
    }),

    // Current refinements (active filters)
    instantsearch.widgets.currentRefinements({
      container: '#algolia-active-filters',
      transformItems: chipsTransform(),
      cssClasses: {
        root: 'ecom-active-filters',
        item: '',
        category: 'ecom-active-filter-tag',
        delete: ''
      }
    }),
  ].concat(mobileChips ? [
    // QW 2026-10-03: the same removable chips above the grid on mobile (#active-filters-mobile)
    instantsearch.widgets.currentRefinements({
      container: '#active-filters-mobile',
      transformItems: chipsTransform(),
      cssClasses: {
        root: 'ecom-active-filters--mobile',
        item: '',
        category: 'ecom-active-filter-tag',
        delete: ''
      }
    })
  ] : []).concat([

    // Clear refinements
    instantsearch.widgets.clearRefinements({
      container: '#algolia-clear-filters',
      templates: {
        resetLabel: function() {
          return '<a class="ecom-filters__clear">Clear All</a>';
        }
      },
      cssClasses: {
        disabledButton: 'hidden'
      }
    }),

    // ---- P1 item 2: Inch | Metric toggle (replaces the Measurement System list; that group stays empty = hidden)
    FX.unitToggle(window.instantsearch, { container: '#ecom-unit-toggle', attribute: 'meta.custom.measurement_system' }),
    // ---- P1 item 7: sort control (Relevance only until Phase 2 adds virtual replicas to EcomFacets.SORTS)
    FX.sortControl(window.instantsearch, { select: '#ecom-sort-select', pills: '#ecom-sort-pills' }),

    // ---- measurable: true size order on the whole list (items 4/5), local find boxes (item 6) ----------------
    filterWidget('thread-size',        'thread_size',        { sort: 'size', limit: 10, find: 'Find a size, e.g. 3/8-16 or M10', noun: 'sizes' }),
    filterWidget('diameter',           'diameter_nominal_',  { sort: 'size', limit: 10, find: 'Find a size...', noun: 'sizes' }),
    filterWidget('length',             'length_nominal_',    { sort: 'size', limit: 10, find: 'Find a length...', noun: 'lengths' }),
    filterWidget('grip-range',         'grip_range',         { sort: 'size', noun: 'sizes' }),   // rev 5c F2: noun
    filterWidget('rivet-size',         'rivet_size',         { sort: 'size', noun: 'sizes' }),   // rev 5c F2: noun
    filterWidget('outer-diameter',     'outer_diameter',     { sort: 'size', find: 'Search...', noun: 'diameters' }),   // rev 5b: noun
    filterWidget('inside-diameter',    'inside_diameter',    { sort: 'size', find: 'Search...', noun: 'diameters' }),   // rev 5b: noun
    filterWidget('thickness',          'thickness',          { sort: 'size', find: 'Search...', noun: 'thicknesses' }),   // rev 5b: noun
    filterWidget('width-across-flats', 'width_across_flats', { sort: 'size', find: 'Search...', noun: 'sizes' }),   // rev 5c F2: noun

    // ---- strength classes: gradeSort ----------------------------------------
    filterWidget('grade',              'grade',              { sort: 'grade', limit: 10, noun: 'grades' }),   // rev 5c F2: noun

    // ---- P1 (doc §5.1): material / finish / head / drive / types = biggest first (was A-Z, which put Steel last)
    filterWidget('material',           'material',           { sort: 'count', limit: 10, noun: 'materials' }),   // rev 5d: noun (Mira)
    filterWidget('alloy-type',         'material_class',     { sort: 'count', limit: 12, find: 'Search alloys...' }),
    filterWidget('finish',             'finish',             { sort: 'count', limit: 10, find: 'Search finishes...', noun: 'finishes' }),   // rev 5c F2: noun
    filterWidget('head',               'head_style',         { sort: 'count', limit: 9, find: 'Search head styles...', noun: 'styles', tiles: true }),   // Batch C tiles (3 x 3)   // rev 5d: noun (Mira)
    filterWidget('drive',              'drive_type',         { sort: 'count', limit: 9, noun: 'types', tiles: true }),   // Batch C tiles   // rev 5d: noun (Mira)
    filterWidget('threading',          'thread_type'),   // was meta.custom.threading, which does not exist
    // measurement-system: now the Inch | Metric toggle above
    filterWidget('standard',           'standard',           { find: 'Search standards...', noun: 'standards' }),   // rev 5c F2: noun
    filterWidget('point-type',         'point_type',         { sort: 'count' }),
    filterWidget('made-in-usa',        'made_in_usa',        { relabel: { 'true': 'Made in USA', 'false': 'Imported' } }),
    filterWidget('mandrel-material',   'mandrel_material',   { sort: 'count' }),

    // ---- added 2 Sep 2026: faceted in Algolia, never surfaced ----------------
    filterWidget('thread-coverage',    'thread_coverage',    { sort: 'count' }),
    filterWidget('thread-series',      'thread_series',      { sort: 'series' }),
    filterWidget('thread-action',      'thread_action',      { sort: 'count' }),
    filterWidget('tapping-type',       'tapping_type',       { sort: 'count' }),
    filterWidget('thread-direction',   'thread_direction',   { sort: 'count' }),
    filterWidget('washer-type',        'washer_type',        { sort: 'count', limit: 9, noun: 'types', tiles: true }),   // Batch C
    filterWidget('nut-type',           'nut_type',           { sort: 'count', limit: 9, noun: 'types', tiles: true }),   // Batch C
    filterWidget('body-style',         'body_style',         { sort: 'count' }),
    filterWidget('sems-type',          'sems_type',          { sort: 'count' }),
    filterWidget('special-feature',    'special_feature',    { sort: 'count' }),
    // P1 item 3: Country of Origin and RoHS stay mounted (old shared URLs keep working) but the rail hides them
    filterWidget('country-of-origin',  'country_of_origin'),
    filterWidget('rohs',               'rohs'),
    // QA2 (2026-10-01): Product Type uses the top-level Algolia attribute (not meta.custom.*). P1: first on mixed pages
    // (doc §4: count desc, top 8 + a find box); on single-family pages only when the results hold 2+ types.
    filterWidget('product-type',       'product_type',       { sort: 'count', limit: 8, find: 'Search product types...', noun: 'types' }),

    // Product hits - now using renderCard
    instantsearch.widgets.hits({
      container: '#algolia-hits',
      templates: {
        item: renderCard,
        empty: function() {
          return '<div style="text-align:center;padding:60px 20px;color:#6b7280;">' +
            '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" style="margin-bottom:16px;opacity:0.5;"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>' +
            '<h3 style="margin:0 0 8px;font-size:18px;color:#1f2937;">No products found</h3>' +
            '<p style="margin:0;">Try adjusting your filters.</p>' +
          '</div>';
        }
      }
    }),

    // Pagination
    instantsearch.widgets.pagination({
      container: '#algolia-pagination',
      padding: 2,
      showFirst: true,
      showLast: true
    })
  ]);
  }

  // ---- QW 2026-10-03: Algolia failure fallback ------------------------------
  // If Algolia can't be reached (outage, quota, blocker, firewall) the Liquid grid,
  // its count and Shopify sort stay as they are, and the sidebar / drawer shows a short
  // message with Liquid links (#filters-unavailable, rendered hidden by the section).
  var ssrCountHtml = null;          // server-rendered "N products", restored on failure
  var algoliaFailed = false;
  var algoliaOk = false;
  var FAIL_TIMEOUT_MS = 12000;      // no first result after this long = treat as unavailable
  function showFiltersUnavailable(reason) {
    if (algoliaOk || algoliaFailed) return;
    algoliaFailed = true;
    root.classList.remove('is-algolia-loading');
    root.classList.add('is-algolia-failed');
    removeFilterTrigger();
    // keep (or put back) the Liquid grid + pagination + count
    if (ssrHits) ssrHits.style.display = '';
    if (ssrPag) ssrPag.style.display = '';
    if (liveHits && ssrHits) liveHits.style.display = 'none';
    if (livePag && ssrHits) livePag.style.display = 'none';
    root.classList.remove('is-algolia-live');
    liveShown = !ssrHits;
    var pcEl = document.getElementById('product-count');
    if (pcEl && ssrCountHtml !== null) pcEl.innerHTML = ssrCountHtml;
    var msg = document.getElementById('filters-unavailable');
    if (msg) msg.hidden = false;
    setMobileChips(0);
    if (window.console) console.warn('[ecom-collection] filters unavailable:', reason);
  }

  // ---- QW 2026-10-03: mobile chips (#active-filters-mobile) + count badge ----------
  var badge = document.getElementById('filter-count-badge');
  var mobileChips = document.getElementById('active-filters-mobile');
  function countRefinements() {
    if (!search) return 0;
    var ui = (search.getUiState() || {})[CONFIG.indexName] || {};
    var rl = ui.refinementList || {}, n = 0;
    Object.keys(rl).forEach(function (k) { n += (rl[k] || []).length; });
    return n;
  }

  // QA fix 2026-10-03: give every chip remove button an accessible name ("Remove filter: Stainless Steel")
  function labelChipDeletes(scope) {
    if (!scope) return;
    scope.querySelectorAll('.ais-CurrentRefinements-category').forEach(function (cat) {
      var del = cat.querySelector('.ais-CurrentRefinements-delete');
      var lab = cat.querySelector('.ais-CurrentRefinements-categoryLabel');
      if (del) del.setAttribute('aria-label', 'Remove filter: ' + (lab ? lab.textContent.trim() : 'filter'));
    });
  }
  function setMobileChips(n) {
    labelChipDeletes(root);
    if (badge) { badge.textContent = n > 0 ? String(n) : ''; badge.style.display = n > 0 ? '' : 'none'; }
    if (mobileChips) mobileChips.style.display = n > 0 ? '' : 'none';
  }

  // ---- start InstantSearch (lazily) ----------------------------------------
  function startSearch() {
    if (IS_BOT) return Promise.resolve(null);
    if (startPromise) return startPromise;

    root.classList.add('is-algolia-loading');
    if (loadBox) { var lb = loadBox.querySelector('button'); if (lb) { lb.textContent = 'Loading filters…'; lb.disabled = true; } }
    startPromise = ensureLibs().then(function () {
      var key = getSearchKey();
      if (!key) throw new Error('no Algolia search key in app config');   // -> catch -> showFiltersUnavailable, 0 calls
      if (!FX) throw new Error('ecom-facets-core.js missing');           // P1: same fallback, 0 calls
      var searchClient = window.algoliasearch(CONFIG.appId, key);

      search = window.instantsearch({
        indexName: CONFIG.indexName,
        searchClient: searchClient,
        routing: true,
        onStateChange: function(opts) {
          opts.setUiState(opts.uiState);
        }
      });

      search.on('error', function (e) { showFiltersUnavailable((e && (e.error || e).message) || 'search error'); });
      search.on('render', function() {
        // InstantSearch also renders after a failed request (status "error"); don't
        // treat that as a working filter panel.
        if (search.status === 'error' || search.error) { showFiltersUnavailable('render with error'); return; }
        if (algoliaFailed) return;
        if (!algoliaOk && search.helper && search.helper.lastResults) { algoliaOk = true; clearTimeout(failTimer); }
        if (!liveShown && userHasRefined()) showLive();
        root.classList.remove('is-algolia-loading');
        removeFilterTrigger();
        setMobileChips(countRefinements());
        // re-bind compare checkboxes after every render (new cards = new checkboxes)
        if (window.EFCompare) window.EFCompare.bindCheckboxes();
        afterRender();
        renderCbs.forEach(function (cb) { try { cb(search); } catch (e) { if (window.console) console.warn('[ecom-collection] render hook', e); } });   // M8b
      });

      var configureParams = {
        hitsPerPage: HITS_PER_PAGE,
        maxValuesPerFacet: 1000,
        distinct: true,
        facetingAfterDistinct: true
      };
      if (collectionHandle && collectionHandle !== 'all') {
        configureParams.filters = 'collections:' + collectionHandle;
      }
      search.addWidgets([ window.instantsearch.widgets.configure(configureParams) ]);
      // The stats widget appends into #product-count, so clear the server-rendered count first
      // (kept in ssrCountHtml so a failure can put it back)
      var pcEl = document.getElementById('product-count');
      if (pcEl) { if (ssrCountHtml === null) ssrCountHtml = pcEl.innerHTML; pcEl.innerHTML = ''; }
      search.addWidgets(buildWidgets());
      search.start();
      return search;
    }).catch(function (err) {
      if (window.console) console.warn('[ecom-collection] Algolia start failed', err);
      showFiltersUnavailable('start failed');
      return null;
    });
    var failTimer = setTimeout(function () { showFiltersUnavailable('timeout'); }, FAIL_TIMEOUT_MS);
    return startPromise;
  }

  // ---- visible "Show filters" trigger -------------------------------------------
  // The theme CSS hides every filter group until it has 2+ options
  // (details.ecom-filter-group:not(:has(li:nth-child(2)))), so before Algolia
  // starts the sidebar would look empty. Insert one visible trigger (humans only);
  // it is removed on the first Algolia render.
  var HOVER_INTENT_MS = 400; // start after the pointer rests on the sidebar this long; 0 = disabled
  var loadBox = null;
  function addFilterTrigger() {
    if (IS_BOT || !sidebar) return;
    var host = document.getElementById('algolia-filters') || sidebar;
    var header = host.querySelector('.ecom-filters__header');
    loadBox = document.createElement('div');
    loadBox.className = 'ecom-filters-load';
    loadBox.style.cssText = 'padding:16px 20px;';
    // QA P1 F9 (2026-10-08): brand navy #002F6C (was #002868)
    loadBox.innerHTML =
      '<button type="button" data-algolia-start class="ecom-filters-load__btn" ' +
      'style="width:100%;padding:10px 14px;border:1px solid #002F6C;border-radius:6px;background:#002F6C;color:#fff;font-size:14px;font-weight:600;cursor:pointer;">' +
      'Show filters</button>' +
      '<p style="margin:8px 0 0;font-size:13px;color:#64748b;line-height:1.4;">Narrow by size, thread, length, material, grade, finish and more.</p>';
    if (header && header.nextSibling) host.insertBefore(loadBox, header.nextSibling);
    else host.insertBefore(loadBox, host.firstChild);
  }
  function removeFilterTrigger() {
    if (loadBox && loadBox.parentNode) loadBox.parentNode.removeChild(loadBox);
    loadBox = null;
  }

  // ---- interaction triggers ----------------------------------------------------
  function onFirstInteraction() {
    startSearch();
  }
  if (sidebar) {
    ['click', 'focusin', 'touchstart', 'keydown'].forEach(function (evt) {
      sidebar.addEventListener(evt, onFirstInteraction, { once: true, passive: true });
    });
    if (HOVER_INTENT_MS > 0 && window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      var hoverTimer = null;
      sidebar.addEventListener('pointerenter', function () {
        hoverTimer = setTimeout(onFirstInteraction, HOVER_INTENT_MS);
      });
      sidebar.addEventListener('pointerleave', function () { clearTimeout(hoverTimer); });
    }
  }
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-algolia-start]')) {
      e.preventDefault();
      startSearch().then(function (s) {
        // explicit "show all / filter" button (option B) -> reveal live grid right away
        if (s && !algoliaFailed && e.target.closest('#algolia-start')) showLive();
      });
    }
  });

  // ---- mobile filter panel (no network) -------------------------------------------
  // P1 item 8: at <= 1024px the sidebar is a full-screen modal dialog (role=dialog, aria-modal, focus trap, Esc,
  // focus back to the Filters button), 44px close, sticky footer "Clear all" + "Show N products". Opening it
  // starts InstantSearch exactly as before (one request); opening/closing again costs nothing.
  var mft = document.getElementById('mobile-filter-toggle');
  var cf = document.getElementById('close-filters');
  var sheet = FX ? FX.drawer({
    sheet: sidebar, backdrop: backdrop, openers: [mft], closeBtn: cf, labelledBy: 'filters-title',
    onOpen: function () { startSearch(); }
  }) : null;
  function closeMobileFilters() {
    if (sheet) { sheet.close(); return; }
    if (sidebar) sidebar.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-visible');
    document.body.style.overflow = '';
  }
  if (!sheet) {   // core missing: the live behaviour
    if (mft) mft.addEventListener('click', function () { startSearch(); if (sidebar) sidebar.classList.add('is-open'); if (backdrop) backdrop.classList.add('is-visible'); document.body.style.overflow = 'hidden'; });
    if (cf) cf.addEventListener('click', closeMobileFilters);
    if (backdrop) backdrop.addEventListener('click', closeMobileFilters);
  }
  window.closeMobileFilters = closeMobileFilters;
  var showBtn = document.getElementById('ecom-drawer-show');
  var clearBtn = document.getElementById('ecom-drawer-clear');
  if (showBtn) showBtn.addEventListener('click', function () {
    // the grid behind already shows the refined results once the shopper has filtered; reveal it in any case
    if (search && !algoliaFailed && userHasRefined()) showLive();
    closeMobileFilters();
  });
  if (clearBtn) clearBtn.addEventListener('click', function () {
    if (!search) return;
    var h = search.mainIndex.getHelper();
    h.clearRefinements().search();   // one request; keeps the collection filter (it is a configure param)
  });

  // P1: per-family rail + footer count + unit prices, after every InstantSearch render (no network)
  var rail = FX ? new FX.Rail(root.querySelector('#algolia-filters .ecom-filters__form'), { handle: collectionHandle }) : null;
  function afterRender() {
    if (!FX || !search) return;
    var res = search.mainIndex.getResults();
    if (rail && res) rail.apply(res, search.mainIndex.getHelper().state);
    if (showBtn && res) showBtn.textContent = FX.showCountLabel(res.nbHits, res.nbHits === 1 ? 'product' : 'products');
    FX.fillPPP(liveHits || root);
  }
  // server-rendered cards: unit prices once the deferred ecom-ppp scripts have run (they run before DOMContentLoaded)
  function fillSsrPPP() { if (FX) FX.fillPPP(ssrHits || root); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fillSsrPPP); else fillSsrPPP();

  // ===== Add to Cart (unchanged logic; delegated on #algolia-collection so it
  // works for both Liquid-rendered and Algolia-rendered cards) =====
  var toast = document.getElementById('ae-cart-toast');
  var toastMsg = document.getElementById('ae-cart-toast-msg');
  var toastTimer;
  var ATC_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg> Add to Cart';

  function showToast(msg, isError) {
    if (!toast || !toastMsg) return;
    clearTimeout(toastTimer);
    toastMsg.textContent = msg;
    toast.style.background = isError ? '#dc2626' : '#111827';
    toast.classList.add('is-visible');
    toastTimer = setTimeout(function() { toast.classList.remove('is-visible'); }, 3000);
  }

  function addToCart(variantId, btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="ae-atc-spinner"></span> Adding...';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
    .then(function(res) {
      if (!res.ok) return res.json().then(function(e) { throw new Error(e.description || 'Could not add item'); });
      return res.json();
    })
    .then(function(data) {
      btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg> Added!';
      btn.classList.add('ae-atc-btn--success');
      showToast(data.title + ' added to cart');
      fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          var counters = document.querySelectorAll('[data-cart-count], .cart-count, .header__cart-count, #cart-count');
          counters.forEach(function(el) { el.textContent = cart.item_count; });
        })
        .catch(function() {});
      setTimeout(function() {
        btn.innerHTML = ATC_ICON;
        btn.classList.remove('ae-atc-btn--success');
        btn.disabled = false;
      }, 2500);
    })
    .catch(function(err) {
      btn.innerHTML = 'View Product';
      btn.classList.add('ae-atc-btn--error');
      showToast(err.message || 'Could not add to cart', true);
      if (window.console) console.error('[Algolia ATC]', err);
      setTimeout(function() {
        btn.innerHTML = ATC_ICON;
        btn.classList.remove('ae-atc-btn--error');
        btn.disabled = false;
      }, 2500);
    });
  }

  root.addEventListener('click', function(e) {
    var btn = e.target.closest('.ae-atc-btn[data-variant-id]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    e.stopPropagation();
    addToCart(btn.getAttribute('data-variant-id'), btn);
  });

  // ---- M8b (2026-10-09): hooks for the grouped-table List view (assets/ecom-grouped-table.js) -----------
  // Read-only access + a search client that does NOT start InstantSearch (no request until the List asks).
  var renderCbs = [];
  var gtClient = null;
  window.EcomCollection = {
    getSearch: function () { return search; },
    startSearch: startSearch,
    baseFilters: function () { return (collectionHandle && collectionHandle !== 'all') ? 'collections:' + collectionHandle : ''; },
    searchClient: function () {
      if (IS_BOT) return null;
      if (!gtClient) gtClient = ensureLibs().then(function () {
        var key = getSearchKey();
        if (!key) throw new Error('no Algolia search key in app config');
        return window.algoliasearch(CONFIG.appId, key);
      });
      return gtClient;
    },
    toast: function (m, err) { showToast(m, err); },
    // M8b r3: resolves once a filtered URL (?shopify_products[...]) has been applied by InstantSearch (first render), so the
    // List never plans unfiltered first. No URL state (or a bot): resolves at once, no request.
    ready: function () {
      if (IS_BOT || !hasAlgoliaUrlState()) return Promise.resolve();
      return startSearch().then(function (s) {
        if (!s || (s.helper && s.helper.lastResults)) return;
        return new Promise(function (res) { var done = false, cb = function () { if (!done) { done = true; res(); } }; renderCbs.push(cb); setTimeout(cb, 8000); });
      });
    },
    onRender: function (cb) { renderCbs.push(cb); }
  };

  // ---- boot ---------------------------------------------------------------------
  addFilterTrigger();
  if (ssrHits && liveHits && !liveShown) liveHits.style.display = 'none';
  if (ssrPag && livePag && !liveShown) livePag.style.display = 'none';

  if (!IS_BOT && hasAlgoliaUrlState()) {
    // Shared / bookmarked filtered URL: start right away (after the Algolia app
    // config at the end of <body> has been parsed so its key is available).
    var go = function () { startSearch().then(function (s) { if (s && !algoliaFailed) showLive(); }); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go);
    else go();
  }
})();
